import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import clsx from 'clsx'
import { GraphQL } from '@guestgoodie/graphql'
import { dateTime } from '@guestgoodie/helpers'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon, SubtitleIcon } from '@hugeicons/core-solid-rounded'
import * as Popover from '@radix-ui/react-popover'
import { Trans, useLingui } from '@lingui/react/macro'
import { PlusSignIcon } from '@hugeicons/core-stroke-rounded'
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form'

import * as Form from '@/features/form'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { popoverContainer } from '@/utils/popovers'
import { useBranding } from '@/features/branding'
import { FormTranslations } from '@/features/translations'

interface Props {
  captions?: GraphQL.Caption[] | null
  src: string
  locale: GraphQL.AppLocale
  onChange: (captions: GraphQL.Caption[]) => void
}

function Caption({
  max,
  value,
  onChange,
  onDelete
}: {
  value?: GraphQL.Caption
  max?: string
  onChange: (caption: GraphQL.Caption) => void
  onDelete: () => void
}) {
  const { t } = useLingui()

  return (
    <div className="flex w-full flex-col gap-1 py-1">
      <div className="flex w-full flex-row items-center justify-between gap-1">
        <div className="flex flex-row items-center gap-2">
          <Input
            type="time"
            value={dateTime.formatSecondsToTime(value?.startAt ?? 0)}
            onChange={event => {
              if (!value) return

              onChange({
                ...value,
                startAt: dateTime.timeToSeconds(event.target.value)
              })
            }}
            min="00:00"
            max={max}
          />
          <Input
            type="time"
            value={dateTime.formatSecondsToTime(value?.endAt ?? 0)}
            onChange={event => {
              if (!value) return

              onChange({
                ...value,
                endAt: dateTime.timeToSeconds(event.target.value)
              })
            }}
            min="00:00"
            max={max}
          />
        </div>
        <Button onClick={onDelete} Icon={Delete02Icon} />
      </div>
      <Input
        type="text"
        value={value?.text ?? ''}
        placeholder={t`Subtitle text`}
        onChange={event => {
          if (!value) return

          onChange({
            ...value,
            text: event.target.value
          })
        }}
      />
    </div>
  )
}

function CaptionsInput({
  duration,
  value,
  locale,
  invalid,
  onChange,
  getValues,
  setValue,
  defaultLocale
}: {
  duration: number
  value?: GraphQL.Caption[]
  locale: GraphQL.AppLocale
  defaultLocale: GraphQL.AppLocale
  invalid?: boolean
  onChange: (captions: GraphQL.Caption[]) => void
  setValue: UseFormSetValue<CaptionsData>
  getValues: UseFormGetValues<CaptionsData>
}) {
  const max = useMemo(() => dateTime.formatSecondsToTime(duration), [duration])

  const lastCaption = useMemo(() => {
    if (!value) return null

    return value[value.length - 1]
  }, [value])

  const onAdd = useCallback(() => {
    const defaultTime = lastCaption?.endAt ?? 0
    const endTime = duration ?? defaultTime

    onChange([
      ...(value ?? []),
      {
        startAt: defaultTime,
        endAt: isNaN(endTime) ? defaultTime : endTime,
        text: '',
        locale
      }
    ])
  }, [value, onChange, duration, lastCaption, locale])

  const onRemove = useCallback(
    (index: number) => {
      onChange(value?.filter((_, idx) => idx !== index) ?? [])
    },
    [value, onChange]
  )

  const onUpdate = useCallback(
    (caption: GraphQL.Caption, index: number) => {
      onChange(
        value?.map(function (cap, idx) {
          if (idx === index) return caption
          return cap
        }) ?? []
      )
    },
    [value, onChange]
  )

  const onCopyFromDefault = useCallback(() => {
    const { captions } = getValues()

    const newCaptions = captions
      .filter(cap => cap.locale === defaultLocale || !cap.locale)
      .map(cap => ({
        ...cap,
        locale
      }))

    setValue(`translations.${locale}.captions`, newCaptions as never)
  }, [defaultLocale, locale, getValues, setValue])

  return (
    <div
      className={clsx(
        'flex w-full flex-col gap-4 overflow-y-auto',
        invalid && 'border border-red-500'
      )}
      // This is needed because of radix Popover, see: https://github.com/radix-ui/primitives/issues/1159
      onWheel={event => event.stopPropagation()}
      onScroll={event => event.stopPropagation()}
      onTouchMove={event => event.stopPropagation()}
    >
      {value?.map(function (caption, index) {
        return (
          <Caption
            key={index}
            max={max}
            value={caption}
            onDelete={() => onRemove(index)}
            onChange={cap => onUpdate(cap, index)}
          />
        )
      })}
      <div
        className={clsx(
          'flex flex-row',
          defaultLocale !== locale ? 'justify-between' : 'justify-end'
        )}
      >
        {defaultLocale !== locale && (
          <Button onClick={onCopyFromDefault} variant="outline">
            <Trans>Copy form default locale</Trans>
          </Button>
        )}
        <Button onClick={onAdd} Icon={PlusSignIcon}>
          <Trans>Add new</Trans>
        </Button>
      </div>
    </div>
  )
}

const TRANSLATABLE_FIELDS = ['captions']

type CaptionsData = {
  captions: GraphQL.Caption[]
  translations: FormTranslations<GraphQL.Caption[]>
}

interface FormProps {
  captions?: GraphQL.Caption[] | null
  readOnly?: boolean
  duration: number
  onChange: (captions: GraphQL.Caption[]) => void
  onClose: () => void
}

function CaptionsForm({
  captions,
  readOnly,
  duration,
  onChange,
  onClose
}: FormProps) {
  const { t } = useLingui()

  const {
    branding: { defaultLocale }
  } = useBranding()

  const onChangeCaptions = useCallback(
    ({ captions, translations }: CaptionsData) => {
      const trans = Object.entries(translations).flatMap(function ([
        key,
        value
      ]) {
        return (
          value.captions?.map(function (cap) {
            return {
              ...cap,
              locale: key as GraphQL.AppLocale
            }
          }) ?? []
        )
      })

      const newCaptions = [...captions, ...trans]

      onChange(newCaptions)
      onClose()
    },
    [onChange, onClose]
  )

  const { defaultCaptions, translations } = useMemo(() => {
    return (
      captions?.reduce<{
        defaultCaptions: GraphQL.Caption[]
        translations: FormTranslations<GraphQL.Caption[]>
      }>(
        function (acc, caption) {
          if (caption.locale === defaultLocale || !caption.locale) {
            acc.defaultCaptions.push(caption)
          } else {
            const cap = {
              startAt: caption.startAt,
              endAt: caption.endAt,
              text: caption.text
            }

            if (!acc.translations[caption.locale]?.captions) {
              acc.translations[caption.locale] = {
                captions: [cap]
              }
            } else {
              acc.translations[caption.locale]!.captions.push(cap)
            }
          }

          return acc
        },
        {
          defaultCaptions: [],
          translations: {}
        }
      ) ?? {
        defaultCaptions: [],
        translations: {}
      }
    )
  }, [captions, defaultLocale])

  return (
    <Form.Root<CaptionsData>
      onSubmit={onChangeCaptions}
      readOnly={readOnly}
      defaultValues={{
        captions: defaultCaptions,
        translations
      }}
      translatableFields={TRANSLATABLE_FIELDS}
    >
      <Form.Field<CaptionsData>
        name={`captions`}
        label={t`Subtitles`}
        rules={{
          validate: {
            required: Form.Validations.requiredArray(t`This field is required`)
          }
        }}
      >
        {({
          renderProps: { onChange, ...props },
          locale,
          getValues,
          setValue
        }) => (
          <CaptionsInput
            duration={duration}
            onChange={onChange}
            {...props}
            locale={locale}
            getValues={getValues}
            setValue={setValue}
            defaultLocale={defaultLocale}
          />
        )}
      </Form.Field>
      <div className="flex flex-row justify-between items-center">
        <Button type="button" onClick={onClose} variant="outline">
          <Trans>Close</Trans>
        </Button>
        <Form.Submit onlyValidWhenDirty>
          <Trans>Ok</Trans>
        </Form.Submit>
      </div>
    </Form.Root>
  )
}

function AddCaptions({
  src,
  captions: caps,
  onClose,
  onChange,
  locale
}: Props & { onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [duration, setDuration] = useState<number>()

  const captions = useMemo(() => caps ?? [], [caps])

  const localized = useMemo(
    () => captions.filter(c => c.locale === locale),
    [captions, locale]
  )

  useEffect(() => {
    const videoElement = videoRef.current

    if (!videoElement) return

    const handleLoadedMetadata = () => {
      setDuration(Math.floor(videoElement.duration))
    }

    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata)

    // If the video is already loaded, get duration immediately
    if (videoElement.readyState >= 2) handleLoadedMetadata()

    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
    }
  }, [])

  useEffect(() => {
    if (!videoRef.current) return

    const track =
      videoRef.current.textTracks[0] ??
      videoRef.current.addTextTrack('captions')

    Array.from(track.cues ?? []).forEach(cue => track.removeCue(cue))

    localized.forEach(function (caption) {
      const cue = new VTTCue(caption.startAt, caption.endAt, caption.text)

      track.addCue(cue)
    })

    track.mode = 'showing'
  }, [localized])

  return (
    <div className="flex h-full max-h-(--radix-popover-content-available-height) w-[500px] flex-col gap-2 p-4">
      <video src={src} loop controls ref={videoRef} className="hidden h-0" />
      <CaptionsForm
        captions={captions}
        readOnly={false}
        onChange={onChange}
        duration={duration ?? 0}
        onClose={onClose}
      />
    </div>
  )
}

function AddCaptionsPopover(props: Props) {
  const { t } = useLingui()
  const { isAnyDirty } = useContext(Form.DirtyContext)
  const [open, setOpen] = useState(false)

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (isAnyDirty) return
      setOpen(open)
    },
    [isAnyDirty, setOpen]
  )

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger
        className="rounded bg-black/50 p-1 text-white outline-primary hover:bg-black/70"
        aria-label={t`Edit Captions`}
      >
        <HugeiconsIcon icon={SubtitleIcon} className="size-full" />
      </Popover.Trigger>
      <Popover.Portal container={popoverContainer}>
        <Popover.Content
          side="top"
          collisionPadding={20}
          sideOffset={5}
          className="relative overflow-hidden rounded bg-white shadow-xl z-popover"
        >
          <AddCaptions {...props} onClose={() => setOpen(false)} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export default function DirtyAddCaptionsPopover(props: Props) {
  return (
    <Form.DirtyProvider>
      <AddCaptionsPopover {...props} />
    </Form.DirtyProvider>
  )
}
